<template>
  <div>
    <div class="cads-scrim" @click="$emit('close')" />

    <div class="cads-sheet">
      <div class="cads-grabber" />

      <div class="cads-header px-4 pb-3">
        <div class="d-flex align-items-flex-start" style="gap:12px">
          <div class="cads-icon-box" :style="iconBoxStyle">
            <component :is="categoryIcon" :size="22" />
          </div>
          <div style="flex:1; min-width:0">
            <div class="cads-title">{{ sheetTitle }}</div>
            <div class="cads-meta-row">
              <span class="cads-meta-time">{{ sheetMeta }}</span>
              <span v-if="item.invoice" class="cads-invoice-tag">#{{ item.invoice }}</span>
            </div>
            <div class="cads-status-badge mt-2" :style="badgeStyle">{{ statusDisplay }}</div>
          </div>
          <button class="cads-close-btn" @click="$emit('close')">
            <x-icon :size="16" />
          </button>
        </div>
      </div>

      <div class="cads-divider" />

      <!-- ORDER DETAIL -->
      <div v-if="isOrder">
        <div class="px-4 pt-3 pb-1">
          <div class="cads-section-label mb-2">Order Items</div>
          <div v-for="(line, idx) in orderItemLines" :key="idx" class="cads-item-card">
            <div class="cads-item-thumb-wrap">
              <img v-if="line.image" :src="line.image" class="cads-item-thumb" :alt="line.name" />
              <div v-else class="cads-item-thumb-empty">
                <coffee-icon :size="14" />
              </div>
            </div>
            <div class="cads-item-details">
              <div class="cads-item-name">{{ line.name }}</div>
              <div v-if="line.variant" class="cads-item-variant">{{ line.variant }}</div>
              <div class="cads-item-tags">
                <span v-if="line.category" class="cads-tag-category">{{ line.category }}</span>
                <span v-if="line.promoLabel" class="cads-tag-promo">{{ line.promoLabel }}</span>
              </div>
              <div v-if="line.scheduleInfo" class="cads-item-schedule">{{ line.scheduleInfo }}</div>
              <div v-if="line.remark" class="cads-item-remark">{{ line.remark }}</div>
            </div>
            <div class="cads-item-price-col">
              <div class="cads-item-qty">{{ line.qty }}×</div>
              <div class="cads-item-subtotal">{{ line.subtotal }}</div>
            </div>
          </div>
        </div>

        <div class="cads-divider mt-2" />

        <div class="px-4 py-3">
          <div v-if="hasOrderDiscount" class="cads-fee-row">
            <span>Subtotal</span>
            <span>{{ orderSubtotalBeforeDiscount }}</span>
          </div>
          <div v-if="hasOrderDiscount" class="cads-fee-row cads-discount-row">
            <span>Discount</span>
            <span>-{{ orderTotalDiscount }}</span>
          </div>
          <div class="cads-fee-row cads-total-row">
            <span>Total</span>
            <span>{{ orderTotal }}</span>
          </div>
        </div>

        <div v-if="item.notes" class="px-4 pb-3">
          <div class="cads-divider mb-3" />
          <div class="cads-section-label mb-1">Note</div>
          <div class="cads-note-text">{{ item.notes }}</div>
        </div>
      </div>

      <!-- TIP DETAIL -->
      <div v-if="isTip" class="px-4 pt-3 pb-2">

        <!-- Recipient card -->
        <div v-if="tipRecipient" class="cads-recipient-card mb-3">
          <div class="cads-recipient-avatar">
            <img v-if="tipRecipientAvatarUrl" :src="tipRecipientAvatarUrl" class="cads-recipient-avatar-img" :alt="tipRecipient.name" />
            <span v-else class="cads-recipient-initials">{{ tipRecipientInitials }}</span>
          </div>
          <div style="flex:1;min-width:0">
            <div class="cads-recipient-name">{{ tipRecipient.name }}</div>
            <div class="cads-type-badge mt-1" :class="tipReferenceType === 'STAFF' ? 'cads-type-badge--staff' : 'cads-type-badge--dept'">
              <heart-icon v-if="tipReferenceType === 'STAFF'" :size="10" />
              <users-icon v-else :size="10" />
              <span>{{ tipReferenceType === 'STAFF' ? 'Staff' : 'Department' }}</span>
            </div>
          </div>
        </div>

        <div class="cads-fee-row cads-total-row">
          <span>Amount</span>
          <span>{{ tipAmount }}</span>
        </div>

        <div v-if="tipFormattedPayment" class="cads-divider my-3" />
        <div v-if="tipFormattedPayment" class="cads-info-row">
          <span class="cads-info-label">Payment</span>
          <span class="cads-info-value">{{ tipFormattedPayment }}</span>
        </div>

        <div v-if="item.notes" class="cads-note-box mt-3">
          <div class="cads-section-label mb-1">Note</div>
          <div class="cads-note-text">{{ item.notes }}</div>
        </div>
      </div>

      <!-- SERVICE / REQUEST DETAIL (housekeeping + other services) -->
      <div v-if="isServiceRequest" class="px-4 pt-3 pb-2">
        <div v-if="item.notes" class="mb-3">
          <div class="cads-section-label mb-1">Note</div>
          <div class="cads-note-text">{{ item.notes }}</div>
        </div>

        <div v-if="serviceItems.length" class="mb-3">
          <div class="cads-section-label mb-2">Requested Items</div>
          <div v-for="(req, i) in serviceItems" :key="i" class="cads-req-item">
            <check-circle-icon :size="14" class="cads-req-check" />
            {{ req }}
          </div>
        </div>

        <div class="cads-divider my-3" />

        <div class="cads-section-label mb-3">Progress</div>
        <div class="cads-timeline">
          <div
            v-for="(step, i) in timelineSteps"
            :key="i"
            class="cads-timeline-step"
          >
            <div class="cads-tl-left">
              <div class="cads-tl-dot" :class="{ 'cads-tl-dot--done': step.done }" />
              <div v-if="i < timelineSteps.length - 1" class="cads-tl-line" :class="{ 'cads-tl-line--done': step.done && timelineSteps[i+1] && timelineSteps[i+1].done }" />
            </div>
            <div class="cads-tl-content" :class="{ 'cads-tl-content--active': step.done }">
              <div class="cads-tl-label">{{ step.label }}</div>
              <div v-if="step.time" class="cads-tl-time">{{ step.time }}</div>
            </div>
          </div>
        </div>
      </div>

      <div style="height: env(safe-area-inset-bottom, 16px)" />
    </div>
  </div>
</template>

<script>
  import {
    Coffee, Compass, Home, AlertTriangle,
    X as XIcon, CheckCircle as CheckCircleIcon,
    Heart as HeartIcon, Users as UsersIcon,
  } from 'lucide-vue'
  import { formatCurrency, customFormatDateTime, paymentMethodConverter } from '@/utils/index.js'

  const STATUS_MAP = {
    PENDING:            { bg: '#FFF8E6', color: '#B87800' },
    ONPROGRESS:         { bg: '#E6EFF4', color: 'var(--merchant-primary-color)' },
    COMPLETE:           { bg: '#E6F9EE', color: '#1A7A45' },
    CANCELLED:          { bg: '#F3F3F5', color: '#8A929A' },
    WAITING_PAYMENT:    { bg: '#FFF4E6', color: '#E07B27' },
    COMPLETE_CANCELLED: { bg: '#F3F3F5', color: '#8A929A' },
    PENDING_ONPROGRESS: { bg: '#E6EFF4', color: 'var(--merchant-primary-color)' },
  }

  const STATUS_DISPLAY = {
    PENDING:            'Pending',
    ONPROGRESS:         'On Progress',
    COMPLETE:           'Completed',
    CANCELLED:          'Cancelled',
    WAITING_PAYMENT:    'Waiting Payment',
    COMPLETE_CANCELLED: 'Cancelled',
    PENDING_ONPROGRESS: 'On Progress',
  }

  const ICON_MAP = {
    housekeeping:   { bg: '#E6EFF4', color: 'var(--merchant-primary-color)', icon: Home },
    spa:            { bg: '#EAF4F1', color: '#2A7A60', icon: Compass },
    activities:     { bg: '#E6F4F0', color: 'var(--merchant-primary-color)', icon: Compass },
    'report-issue': { bg: '#FEE9E4', color: '#C04A30', icon: AlertTriangle },
    'room-dining':  { bg: '#FEE9E4', color: '#C04A30', icon: Coffee },
    voucher:        { bg: '#F0EBFE', color: '#8B5CF6', icon: Compass },
    tip:            { bg: '#FFF4E6', color: '#E07B27', icon: Coffee },
    order:          { bg: '#FEE9E4', color: '#C04A30', icon: Coffee },
  }

  const STATUS_STEP = {
    PENDING:            1,
    WAITING_PAYMENT:    1,
    PENDING_ONPROGRESS: 2,
    ONPROGRESS:         3,
    COMPLETE:           4,
    CANCELLED:          1,
    COMPLETE_CANCELLED: 1,
  }

  const SKIP_KEYS = ['notes', 'merchant_id', 'module_slug', 'module_merchant_id', 'module_merchant_name', 'reservation_id', 'reference_id', 'reference_type', 'reference_data', 'payment_details', 'amount']

  export default {
    name: 'ConciergeActivityDetailSheet',

    components: { XIcon, CheckCircleIcon, HeartIcon, UsersIcon, CoffeeIcon: Coffee },

    props: {
      item: { type: Object, required: true },
    },

    computed: {
      moduleSlug() {
        return this.item.module && this.item.module.slug
      },

      iconMeta() {
        return ICON_MAP[this.moduleSlug] || ICON_MAP.order
      },

      categoryIcon() {
        return this.iconMeta.icon
      },

      iconBoxStyle() {
        return { background: this.iconMeta.bg, color: this.iconMeta.color }
      },

      badgeStyle() {
        const s = STATUS_MAP[this.item.status]
        return s ? { background: s.bg, color: s.color } : {}
      },

      statusDisplay() {
        return STATUS_DISPLAY[this.item.status] || this.item.status
      },

      sheetTitle() {
        if (this.item.tx_type === 'ORDER') {
          return (this.item.module && this.item.module.name) || 'Order'
        }
        return (this.item.module && this.item.module.name) || 'Service'
      },

      sheetMeta() {
        return customFormatDateTime(this.item.created_at, 'null', 'DD MMM YYYY · HH:mm')
      },

      isOrder() {
        return this.item.tx_type === 'ORDER'
      },

      isTip() {
        return this.item.tx_type === 'SERVICE' && this.moduleSlug === 'tip'
      },

      isServiceRequest() {
        return this.item.tx_type === 'SERVICE' && !this.isTip
      },

      orderItemLines() {
        if (!this.isOrder || !Array.isArray(this.item.tx_detail_list)) return []
        return this.item.tx_detail_list.map(function(d) {
          var image = null
          if (d.product_image_list && d.product_image_list.length) {
            image = d.product_image_list[0].image_url
          } else if (d.product_image && d.product_image.indexOf('noimage') === -1) {
            image = d.product_image
          }
          var discountPct = d.discount_pct && d.discount_pct.parsedValue
          var promoLabel = (discountPct && discountPct < 0) ? (Math.abs(discountPct) + '% OFF') : null
          var scheduleInfo = null
          if (d.use_schedule && d.service_date) {
            var dateStr = customFormatDateTime(d.service_date, 'null', 'DD MMM YYYY · HH:mm')
            scheduleInfo = d.schedule_name ? dateStr + ' · ' + d.schedule_name : dateStr
          }
          var remark = d.remark && d.remark.trim() ? d.remark.replace(/,\s*$/, '').trim() : null
          return {
            name: d.product_name,
            variant: d.variant_name || null,
            category: d.product_category_name || null,
            image: image,
            qty: d.qty_order,
            promoLabel: promoLabel,
            scheduleInfo: scheduleInfo,
            remark: remark,
            subtotal: formatCurrency(parseFloat(d.subtotal_sell), true),
          }
        })
      },

      hasOrderDiscount() {
        if (!this.isOrder || !Array.isArray(this.item.tx_detail_list)) return false
        return this.item.tx_detail_list.some(function(d) {
          return d.discount_pct && d.discount_pct.parsedValue < 0
        })
      },

      orderSubtotalBeforeDiscount() {
        if (!this.isOrder || !Array.isArray(this.item.tx_detail_list)) return ''
        var total = this.item.tx_detail_list.reduce(function(acc, d) {
          return acc + parseFloat(d.subtotal_buy || 0)
        }, 0)
        return formatCurrency(total, true)
      },

      orderTotalDiscount() {
        if (!this.isOrder || !Array.isArray(this.item.tx_detail_list)) return ''
        var subtotalBuy = this.item.tx_detail_list.reduce(function(acc, d) {
          return acc + parseFloat(d.subtotal_buy || 0)
        }, 0)
        var total = parseFloat(this.item.total_payment || 0)
        return formatCurrency(subtotalBuy - total, true)
      },

      orderTotal() {
        return formatCurrency(parseFloat(this.item.total_payment), true)
      },

      tipAmount() {
        if (!this.isTip) return ''
        const details = this.item.tx_detail_list
        const amount = details && details.amount
        return amount ? formatCurrency(parseFloat(amount), true) : ''
      },

      tipPayment() {
        if (!this.isTip) return null
        const details = this.item.tx_detail_list
        return (details && details.payment_details) || null
      },

      tipRecipient() {
        if (!this.isTip) return null
        const details = this.item.tx_detail_list
        return (details && details.reference_data) || null
      },

      tipReferenceType() {
        if (!this.isTip) return null
        const details = this.item.tx_detail_list
        return (details && details.reference_type) || null
      },

      tipRecipientAvatarUrl() {
        const r = this.tipRecipient
        const url = r && r.image_url
        return (url && url.indexOf('http') === 0) ? url : null
      },

      tipRecipientInitials() {
        const r = this.tipRecipient
        const name = r && r.name
        return name ? name.charAt(0).toUpperCase() : '?'
      },

      tipFormattedPayment() {
        const p = this.tipPayment
        if (!p) return null
        return paymentMethodConverter(p.payment_method) || p.payment_method.replace(/_/g, ' ')
      },

      serviceItems() {
        if (!this.isServiceRequest) return []
        const details = this.item.tx_detail_list
        if (!details || typeof details !== 'object' || Array.isArray(details)) return []
        const items = []
        Object.keys(details).forEach(function(key) {
          if (SKIP_KEYS.indexOf(key) !== -1) return
          const val = details[key]
          if (val === true) {
            items.push(key.replace(/-/g, ' '))
          } else if (typeof val === 'number' && val > 0) {
            items.push(val + 'x ' + key.replace(/-/g, ' '))
          }
        })
        return items
      },

      timelineSteps() {
        if (!this.isServiceRequest) return []
        const reached = STATUS_STEP[this.item.status] || 1
        const submittedTime = customFormatDateTime(this.item.created_at, 'null', 'DD MMM · HH:mm')
        return ['Submitted', 'Received', 'In Progress', 'Completed'].map(function(label, i) {
          return {
            label: label,
            done: (i + 1) <= reached,
            time: i === 0 ? submittedTime : null,
          }
        })
      },
    },
  }
</script>

<style scoped>
.cads-scrim {
  position: fixed;
  inset: 0;
  background: rgba(20, 15, 10, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1055;
}

.cads-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1056;
  background: #fff;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding-top: 12px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.18);
  max-height: 88vh;
  overflow-y: auto;
  animation: cadsSheetUp .25s ease-out;
  font-family: Nunito-Regular;
}

@keyframes cadsSheetUp {
  from { transform: translateY(100%) }
  to   { transform: translateY(0) }
}

.cads-grabber {
  width: 40px;
  height: 4px;
  border-radius: 99px;
  background: #E0E8EF;
  margin: 0 auto 16px;
}

.cads-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #F3F6F9;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7886;
  flex-shrink: 0;
}

.cads-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cads-title {
  font-size: 15px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  line-height: 1.3;
  margin-bottom: 2px;
}

.cads-meta-time {
  font-size: 12px;
  font-family: Nunito-Regular;
  color: #6B7886;
}

.cads-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-family: Nunito-Bold;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.cads-divider {
  height: 1px;
  background: #F0F3F6;
}

.cads-section-label {
  font-size: 10px;
  font-family: Nunito-Bold;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6B7886;
}


/* Fee rows */
.cads-fee-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #6B7886;
  padding: 3px 0;
}

.cads-total-row {
  font-size: 14px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  padding-top: 8px;
}

/* Note box */
.cads-note-box {
  background: #F5F8FA;
  border-radius: 12px;
  padding: 12px 14px;
}

.cads-note-text {
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #1C2B36;
  line-height: 1.5;
}

/* Info row */
.cads-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #1C2B36;
}

/* Request items */
.cads-req-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #1C2B36;
  padding: 4px 0;
  text-transform: capitalize;
}

.cads-req-check {
  color: #1A7A45;
  flex-shrink: 0;
}

/* Timeline */
.cads-timeline {
  display: flex;
  flex-direction: column;
}

.cads-timeline-step {
  display: flex;
  gap: 14px;
}

.cads-tl-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
}

.cads-tl-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid #E0E8EF;
  background: #fff;
  flex-shrink: 0;
}

.cads-tl-dot--done {
  background: var(--merchant-primary-color, #2B5F7A);
  border-color: var(--merchant-primary-color, #2B5F7A);
}

.cads-tl-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: #E0E8EF;
  margin: 3px 0;
}

.cads-tl-line--done {
  background: var(--merchant-primary-color, #2B5F7A);
}

.cads-tl-content {
  padding-bottom: 20px;
  flex: 1;
}

.cads-tl-label {
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #B0BAC5;
  line-height: 1.3;
}

.cads-tl-content--active .cads-tl-label {
  font-family: Nunito-SemiBold;
  color: #1C2B36;
}

.cads-tl-time {
  font-size: 11px;
  font-family: Nunito-Regular;
  color: #6B7886;
  margin-top: 2px;
}

/* Meta row with invoice */
.cads-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.cads-invoice-tag {
  font-size: 10px;
  font-family: Nunito-SemiBold;
  color: #6B7886;
  background: #F0F3F6;
  border-radius: 6px;
  padding: 2px 7px;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

/* Recipient card */
.cads-recipient-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F8FAFB;
  border: 1px solid #EAF0F5;
  border-radius: 14px;
  padding: 12px 14px;
}

.cads-recipient-avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--merchant-primary-color, #2B5F7A);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.cads-recipient-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.cads-recipient-initials {
  font-size: 18px;
  font-family: Nunito-Bold;
  color: #fff;
}

.cads-recipient-name {
  font-size: 14px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cads-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-family: Nunito-SemiBold;
}

.cads-type-badge--staff {
  background: #FCE8EF;
  color: #C03060;
}

.cads-type-badge--dept {
  background: #E8EFF8;
  color: #2B5F7A;
}

/* Info rows */
.cads-info-label {
  font-size: 13px;
  font-family: Nunito-Regular;
  color: #6B7886;
  flex-shrink: 0;
}

.cads-info-value {
  font-size: 13px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  margin-left: auto;
}

/* Order item cards */
.cads-item-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F0F3F6;
}
.cads-item-card:last-child {
  border-bottom: none;
}

.cads-item-thumb-wrap {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: #F3F6F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cads-item-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cads-item-thumb-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B0BAC5;
}

.cads-item-details {
  flex: 1;
  min-width: 0;
}

.cads-item-name {
  font-size: 13px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  line-height: 1.3;
  margin-bottom: 2px;
}

.cads-item-variant {
  font-size: 11px;
  font-family: Nunito-Regular;
  color: #6B7886;
  margin-bottom: 4px;
}

.cads-item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px;
}

.cads-tag-category {
  font-size: 10px;
  font-family: Nunito-Regular;
  color: #6B7886;
  background: #F0F3F6;
  border-radius: 5px;
  padding: 2px 6px;
}

.cads-tag-promo {
  font-size: 10px;
  font-family: Nunito-Bold;
  color: #1A7A45;
  background: #E6F9EE;
  border-radius: 5px;
  padding: 2px 6px;
  letter-spacing: 0.2px;
}

.cads-item-schedule {
  font-size: 11px;
  font-family: Nunito-Regular;
  color: var(--merchant-primary-color, #2B5F7A);
  margin-top: 3px;
}

.cads-item-remark {
  font-size: 11px;
  font-family: Nunito-Regular;
  color: #6B7886;
  font-style: italic;
  margin-top: 2px;
}

.cads-item-price-col {
  flex-shrink: 0;
  text-align: right;
}

.cads-item-qty {
  font-size: 11px;
  font-family: Nunito-Regular;
  color: #9AAAB8;
  margin-bottom: 3px;
}

.cads-item-subtotal {
  font-size: 13px;
  font-family: Nunito-SemiBold;
  color: #1C2B36;
  white-space: nowrap;
}

.cads-discount-row {
  color: #1A7A45;
  font-size: 13px;
  font-family: Nunito-Regular;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/ConciergeActivityDetailSheet.vue